import React, { ReactNode, useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { api } from "api/api";
import {
  ExcelImportRequestParams,
  defaultExcelImportRequestParams,
} from "types/excel";
import { useTypedSelector } from "store";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  DialogContentText,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import BackupOutlinedIcon from "@mui/icons-material/BackupOutlined";
import { ActionButtonConfirmStatusWithPolling } from "components";
import { UseProcessPollingOptions } from "hooks/useProcessPolling";
import { ExcelImportProcess, ProcessState } from "types/process";
import { toast } from "react-toastify";
import { ImportConflictAction } from "types/misc";

import ImportFromExcelResultSection from "./ImportFromExcelResultSection";

export default function ImportFromExcelButton() {
  // State
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  // Request Parameters State
  const [deleteUnmatchedSubproperties, setDeleteUnmatchedSubproperties] =
    useState<boolean>(false);
  const [importProperties, setImportProperties] = useState<boolean>(true);
  const [importTables, setImportTables] = useState<boolean>(false);
  const [conflictAction, setConflictAction] = useState<ImportConflictAction>(
    ImportConflictAction.Abort
  );

  // Process object with completion state
  const [activeProcess, setActiveProcess] = useState<ExcelImportProcess | null>(
    null
  );

  // Redux State
  const productLineId = useTypedSelector(
    (state) => state.products.selectedProductLine?.id
  );

  const importDisabled = !productLineId;

  /** Dialog open reset (mirrors ExportProductlineToExcelButton) */
  const onDialogOpen = () => {
    setSelectedFile(null);
    setFileError(null);

    setDeleteUnmatchedSubproperties(false);
    setImportProperties(true);
    setImportTables(false);
    setConflictAction(ImportConflictAction.Abort);
    setActiveProcess(null);
  };

  /** --- File selection + validation --- */
  const onFileChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const file = e.target.files?.[0] ?? null;
    setFileError(null);
    setSelectedFile(file);

    if (!file) return;

    const MAX_BYTES = 50 * 1024 * 1024;
    if (file.size > MAX_BYTES) {
      setFileError("The selected file exceeds the 50 MB limit.");
      setSelectedFile(null);
      return;
    }

    const name = file.name.toLowerCase();
    const validExt = name.endsWith(".xlsx");

    // Known MIME types for Excel OOXML + legacy
    const validMime =
      file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.type === "application/vnd.ms-excel" ||
      file.type === ""; // some browsers report empty; fall back to extension check

    if (!validExt && !validMime) {
      setFileError("Please choose a valid Excel file (.xlsx)");
      setSelectedFile(null);
    }
  };

  /** --- startRequest (kicks off backend import) --- */
  const startRequest = async (): Promise<ExcelImportProcess> => {
    if (!productLineId) {
      throw new Error("No productline selected.");
    }

    if (!selectedFile) {
      throw new Error("Select an Excel file before importing.");
    }

    // Controller allows only .xlsx; guard here too.
    const lower = selectedFile.name.toLowerCase();
    if (!lower.endsWith(".xlsx")) {
      throw new Error("Only .xlsx files are supported.");
    }

    // The server requires at least one of ImportProperties or ImportTables to be true.
    if (!importProperties && !importTables) {
      throw new Error(
        "Enable at least one of 'Import properties' or 'Import tables'."
      );
    }

    // Compose the import params from current state
    const importParams: ExcelImportRequestParams = {
      ...defaultExcelImportRequestParams,
      importProperties,
      importTables,
      conflictAction,
      deleteUnmatchedSubproperties,
      // If/when we expose sheet filtering, add:
      // worksheetNames: ["Sheet1", "Sheet2"],
    };

    // Call the wrapper that posts multipart/form-data with file + fields
    const response = await api.importFromExcel(
      productLineId,
      selectedFile,
      importParams
    );
    return response.data as ExcelImportProcess;
  };

  /** --- pollRequest (same pattern used by your export flow) --- */
  const pollRequest = (id: string): Promise<ExcelImportProcess> => {
    // Identical to your export polling pattern
    const response = api
      .getProcess(id)
      .then((r) => r.data as ExcelImportProcess);
    return response;
  };

  /** --- onTerminal (what to do when the process ends) --- */
  const onTerminal = async (process: ExcelImportProcess): Promise<void> => {
    setActiveProcess(process);

    if (process.processState === ProcessState.COMPLETE) {
      const doneMsg = process.message ?? `Import complete`;
      toast.success(doneMsg);
    } else if (process.processState === ProcessState.ERROR) {
      // Surface a backend message if available (ModalDialog will also show the error)
      if (process.message) toast.error(process.message);
    }
  };

  /** --- Polling options (wire-only object; mirrors your export button) --- */
  const processPollingOptions: UseProcessPollingOptions<
    ExcelImportProcess,
    ExcelImportProcess
  > = {
    startRequest,
    pollRequest,
    pollIntervalMs: 2000,
    onTerminal,
  };

  /** --- Dialog content section (file picker + options) --- */
  const contentSection = useMemo<ReactNode>(() => {
    const fileLabel = selectedFile
      ? `Selected: ${selectedFile.name}`
      : "Choose Excel (.xlsx)…";

    return (
      <ContentStack>
        <DialogContentText>
          Select an Excel workbook and choose how to handle conflicts,
          properties, and tables. Click <strong>Import</strong> to start the
          background process.
        </DialogContentText>

        {/* File picker */}
        <Box>
          <Button
            variant="outlined"
            startIcon={<BackupOutlinedIcon />}
            component="label"
            sx={{ textTransform: "none" }}
          >
            {fileLabel}
            <input
              hidden
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={onFileChange}
            />
          </Button>

          {fileError && (
            <Box mt={1}>
              <Alert severity="error">{fileError}</Alert>
            </Box>
          )}
        </Box>

        {/* Import scope */}
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={importProperties}
                onChange={(e) => setImportProperties(e.target.checked)}
              />
            }
            label="Import properties"
          />
          {/* <FormControlLabel
            control={
              <Checkbox
                checked={importTables}
                disabled={true}
                onChange={(e) => setImportTables(e.target.checked)}
              />
            }
            label="Import tables (Feature not yet available)"
          /> */}
        </FormGroup>

        {/* Conflict handling + destructive option */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems="flex-start"
        >
          <Box sx={{ minWidth: 260 }}>
            <Typography variant="body1" sx={{ ml: 0.5 }}>
              Conflict Action:
            </Typography>
            <Select
              fullWidth
              size="small"
              value={conflictAction}
              onChange={(e) =>
                setConflictAction(e.target.value as ImportConflictAction)
              }
            >
              <MenuItem value={ImportConflictAction.Abort}>
                <strong>Skip</strong>: Do NOT overwrite an existing Property or
                Table
              </MenuItem>
              <MenuItem value={ImportConflictAction.Replace}>
                <strong>Replace</strong>: Automatically overwrite an existing
                Property or Table
              </MenuItem>
            </Select>
          </Box>

          {/* <FormControlLabel
            sx={{ mt: { xs: 1, sm: 3 } }}
            control={
              <Checkbox
                checked={deleteUnmatchedSubproperties}
                onChange={(e) =>
                  setDeleteUnmatchedSubproperties(e.target.checked)
                }
                disabled={conflictAction !== ImportConflictAction.Replace}
              />
            }
            label="Delete unmatched subproperties (only when Replacing)"
          /> */}
        </Stack>

        <DialogContentText sx={{ mt: 1 }}>
          Only <strong>.xlsx</strong> files are supported. Large files may take
          time to process.
        </DialogContentText>
      </ContentStack>
    );
  }, [
    selectedFile,
    fileError,
    importProperties,
    importTables,
    deleteUnmatchedSubproperties,
    conflictAction,
  ]);

  const buildResultSection = useCallback(
    (process: ExcelImportProcess) => (
      <ImportFromExcelResultSection process={process} />
    ),
    []
  );

  return (
    <ActionButtonConfirmStatusWithPolling<
      ExcelImportProcess,
      ExcelImportProcess
    >
      // Button
      variant="outlined"
      color="primary"
      disabled={importDisabled}
      icon={BackupOutlinedIcon}
      iconOnly={false}
      tooltipText="Import changes from Excel"
      tooltipArrow
      tooltipPlacement="right"
      // Dialog
      maxWidth={
        activeProcess && activeProcess.skippedProperties.length > 0
          ? "xl"
          : "sm"
      }
      fullWidth
      dialogTitle="Import from Excel"
      disableEscapeKeyDown
      disableCloseOnBackdropClick
      // Content
      contentSection={contentSection}
      hideContentOnProcessComplete={true}
      // Action texts
      defaultButton="OK"
      cancelButtonText="Cancel"
      okButtonText="Import"
      // Polling
      processPollingOptions={processPollingOptions}
      buildResultSection={buildResultSection}
      // Lifecycle
      onDialogOpen={onDialogOpen}
      resetOnOpen
      // Extra validation: disable OK if no file or invalid
      okButtonDisabled={!selectedFile || !!fileError}
    >
      Import from Excel
    </ActionButtonConfirmStatusWithPolling>
  );
}

/** --- Styled bits (mirrors ExportProductlineToExcelButton’s style) --- */
const ContentStack = styled(Stack)`
  gap: 12px;
`;
